import React, {useContext, useState, useEffect} from 'react'
import {Link as RouteLink} from 'react-router-dom'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Progress} from '@chakra-ui/react'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Text,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    Link,
    DrawerBody,
    useStyleConfig,
    Image,
    Box,
    Skeleton,
    Heading,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getUrl} from '../utils/cloudinary-utils'
import {BASKET_URL} from '../constants'
import {getTotalQuantity} from '../utils/basket-utils'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import CustomApi from './use-custom-api'
import {useSiteContext} from '../contexts/site'
import {useGlobaleContext} from '../contexts/globale'
import useMonetate from './use-monetate'
import {yieldToMain} from '../utils/common-utils'
import LoadingSpinner from '../components/loading-spinner'
import useDeferredNavigation from './use-deferred-navigation'

export const AddToCartDrawerContext = React.createContext()
export const useAddToCartDrawerContext = () => useContext(AddToCartDrawerContext)

export const AddToCartDrawerProvider = ({children}) => {
    const addToCartDrawer = useAddToCartDrawer()
    return (
        <AddToCartDrawerContext.Provider value={addToCartDrawer}>
            {children}
            <AddToCartDrawer />
        </AddToCartDrawerContext.Provider>
    )
}
AddToCartDrawerProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export const AddToCartDrawer = () => {
    const {isOpen, onClose, data} = useAddToCartDrawerContext()
    const {product, itemsAdded, priceData, heroImage} = data || {}
    const {getTokenWhenReady} = useAccessToken()
    const customapi = new CustomApi()
    const {isYR} = useSiteContext()
    const {countryISO, currencyCode} = useGlobaleContext()
    const variant = isYR ? 'yr' : 'ks'
    const drawerStyles = useStyleConfig('AddToCartDrawerStyles')
    const [discountMsg, setDiscountMsg] = useState('')
    const [remainingPercent, setRemainingPercent] = useState(0)
    const [detailsFetched, setDetailsFetched] = useState(false)
    const [actionSpinner, showActionSpinner] = useState(false)
    const [pricesToDisplay, setPricesToDisplay] = useState(null)
    const intl = useIntl()
    const monetate = useMonetate()
    const deferredNavigate = useDeferredNavigation()
    const {
        app: {commerceAPI}
    } = getConfig()
    const api = useCommerceApi()
    const {proxy} = api.shopperExperience.clientConfig
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}

    let changeUpProductID = null
    try {
        const jsonConfig = JSON.parse(configChangeup)
        changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
    } catch (e) {
        console.log(e.message)
    }

    const {data: basket = {}} = useCurrentBasket()
    const [totalQuantity, setTotalQuantity] = useState(0)

    const quantityTotal = basket?.productItems?.reduce((s, i) => s + i?.quantity, 0)
    const giftQtyTotal = basket?.giftCertificateItems?.length
    useEffect(() => {
        setTotalQuantity(getTotalQuantity(basket, changeUpProductID))
    }, [quantityTotal, giftQtyTotal])
    const totalGcAmount =
        basket?.giftCertificateItems?.reduce((sum, item) => sum + item.amount, 0) || 0

    useEffect(() => {
        const getToken = async () => {
            const token = await getTokenWhenReady()
            const parameters = {
                c_currencyCode: currencyCode,
                c_countryISO: countryISO,
                c_subTotal: totalGcAmount + basket?.productSubTotal || basket?.orderTotal
            }
            const response = await customapi.get(token, 'discounts-info', '/', parameters, proxy)
            setDetailsFetched(true)
            setPricesToDisplay({
                productPrice: response?.productPrice,
                subTotal: totalGcAmount + basket?.productSubTotal || basket?.orderTotal // the basket total is already in international currency
            })
            if (response?.approachingDiscounts?.length) {
                setDiscountMsg(response?.approachingDiscounts?.[0]?.discountMsg)
                setRemainingPercent(response?.approachingDiscounts?.[0]?.remainingPercent)
            }
            if (response?.appliedShippingPromo?.length) {
                setDiscountMsg(response?.appliedShippingPromo?.[0]?.discountMsg)
                setRemainingPercent(response?.appliedShippingPromo?.[0]?.remainingPercent)
            }
        }
        if (isOpen) {
            getToken()
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) {
            setDiscountMsg('')
            setRemainingPercent(0)
            setDetailsFetched(false)
            setPricesToDisplay(null)
        }
    }, [isOpen])

    if (!product?.currency) return null

    const currentPriceText = priceData?.currentPrice
        ? intl.formatNumber(priceData?.currentPrice, {
              style: 'currency',
              currency: product?.c_selectedCurrency || product?.currency
          })
        : null
    const listPriceText = priceData?.listPrice
        ? intl.formatNumber(priceData?.listPrice, {
              style: 'currency',
              currency: product?.c_selectedCurrency || product?.currency
          })
        : null
    const subTotalText = pricesToDisplay?.subTotal
        ? intl.formatNumber(pricesToDisplay?.subTotal, {
              style: 'currency',
              currency: product?.c_selectedCurrency || product?.currency
          })
        : null
    const closeDrawer = async () => {
        showActionSpinner(true)
        await yieldToMain()
        onClose()
        const {
            idString,
            breadcrumb,
            customer,
            wishlistLength,
            hits = [],
            searchTerm,
            itemsAdded
        } = window.mtPreviousData
        // As per the requirement, we should send the last event when the user closes the drawer.
        monetate.monetateTrackPage(
            idString,
            breadcrumb,
            basket?.productItems,
            customer,
            wishlistLength,
            hits,
            searchTerm,
            itemsAdded,
            basket?.giftCertificateItems
        )
        showActionSpinner(false)
    }
    return (
        <Box>
            {actionSpinner && <LoadingSpinner position="fixed" top="auto" />}
            <Drawer
                isOpen={isOpen}
                onClose={closeDrawer}
                scrollBehavior="inside"
                blockScrollOnMount={false}
                isCentered
                trapFocus={false} // stops focus trap
                returnFocusOnClose={false} // prevents scroll jump
            >
                <DrawerOverlay background="blackAlpha600" height="100%" />
                <DrawerContent marginTop={0} {...drawerStyles.main.content}>
                    <Box {...drawerStyles.main.closeIconWrapper} onClick={closeDrawer}>
                        <Image
                            width="15px"
                            height="15px"
                            alt="Cross Icon"
                            src={getUrl('UIImages/images/crossed-icon.svg')}
                        />
                    </Box>
                    <DrawerBody {...drawerStyles.main.drawerBody}>
                        {countryISO === 'US' ? (
                            detailsFetched ? (
                                <Box
                                    className="progress-wrapper"
                                    display={discountMsg ? 'block' : 'none'}
                                >
                                    <Box
                                        {...drawerStyles.main.progressText}
                                        dangerouslySetInnerHTML={{__html: discountMsg}}
                                    />
                                    <Progress
                                        value={remainingPercent || 100 - remainingPercent}
                                        variant={variant}
                                        size="discount"
                                    />
                                </Box>
                            ) : (
                                <Box>
                                    <Skeleton height="25px" width="60%" marginBottom="5px" />
                                    <Skeleton height="15px" width="100%" />
                                </Box>
                            )
                        ) : (
                            <></>
                        )}
                        <Link {...drawerStyles.main.productInfo} as={RouteLink} to={BASKET_URL}>
                            <Heading
                                as="h4"
                                {...drawerStyles.main.heading}
                                display={{base: 'block', lg: 'none'}}
                            >
                                <FormattedMessage
                                    id="added.to.your.bag"
                                    defaultMessage="Added to Your Bag"
                                />
                            </Heading>
                            <Box display="flex" gap="20px">
                                <Box {...drawerStyles.main.productImageBox}>
                                    <Image
                                        src={
                                            document
                                                .querySelector(
                                                    '.engraved-images .engraved-img-1 img'
                                                )
                                                ?.getAttribute('src') ||
                                            heroImage ||
                                            product?.c_images?.smallImage
                                        }
                                        alt={product?.name}
                                        {...drawerStyles.main.productImage}
                                    />
                                </Box>
                                <Box {...drawerStyles.main.priceNameWrapper}>
                                    <Heading
                                        as="h4"
                                        {...drawerStyles.main.heading}
                                        display={{base: 'none', lg: 'block'}}
                                    >
                                        <FormattedMessage
                                            id="added.to.your.bag"
                                            defaultMessage="Added to Your Bag"
                                        />
                                    </Heading>
                                    <Text noOfLines={2} {...drawerStyles.main.productName}>
                                        {product?.name}
                                    </Text>
                                    <Box {...drawerStyles.main.priceWrapper}>
                                        {currentPriceText !== listPriceText && listPriceText && (
                                            <Text {...drawerStyles.main.salePrice}>
                                                {listPriceText?.replace(
                                                    product?.c_selectedCurrency ||
                                                        product?.currency,
                                                    product?.c_selectedCurrencySymbol
                                                )}
                                            </Text>
                                        )}
                                        <Text
                                            {...drawerStyles.main.productPrice}
                                            color={
                                                currentPriceText !== listPriceText && listPriceText
                                                    ? 'purple.600'
                                                    : 'black'
                                            }
                                            fontWeight={
                                                currentPriceText !== listPriceText && listPriceText
                                                    ? '400'
                                                    : '300'
                                            }
                                        >
                                            {itemsAdded?.length > 1 ? (
                                                <FormattedMessage
                                                    id="and.more"
                                                    defaultMessage="...and {count} more."
                                                    values={{
                                                        count: itemsAdded?.length - 1
                                                    }}
                                                />
                                            ) : currentPriceText ? (
                                                currentPriceText?.replace(
                                                    product?.c_selectedCurrency ||
                                                        product?.currency,
                                                    product?.c_selectedCurrencySymbol
                                                )
                                            ) : (
                                                <Skeleton width="70px" height="20px" />
                                            )}
                                        </Text>
                                    </Box>
                                </Box>
                            </Box>
                        </Link>
                        <Box display="flex" justifyContent="space-between">
                            <Text {...drawerStyles.main.subtotalLabel}>
                                <FormattedMessage id="subtotal" defaultMessage="Subtotal" />
                            </Text>
                            <Text {...drawerStyles.main.subtotalValue}>
                                {subTotalText ? (
                                    subTotalText?.replace(
                                        product?.c_selectedCurrency || product?.currency,
                                        product?.c_selectedCurrencySymbol
                                    )
                                ) : (
                                    <Skeleton width="70px" height="20px" />
                                )}
                            </Text>
                        </Box>
                        <Link
                            as={RouteLink}
                            to={BASKET_URL}
                            onClick={(event) => deferredNavigate(event, BASKET_URL)}
                            {...drawerStyles.main.viewBagButton}
                            bg={isYR ? 'black.300' : 'teal.500'}
                            _hover={{
                                bg: isYR ? 'black.50' : 'teal.400'
                            }}
                        >
                            <FormattedMessage
                                id="view.bag"
                                defaultMessage="View Bag ({totalQuantity})"
                                values={{totalQuantity}}
                            />
                        </Link>

                        <Flex alignItems="center" justifyContent="center" marginTop="-15px">
                            <Image
                                width="23px"
                                height="23px"
                                marginRight="10px"
                                alt="Return Policy Icon"
                                src={getUrl('UIImages/images/free-return.svg')}
                            />
                            <Text color="black.100" fontSize="13px" fontWeight="300">
                                <FormattedMessage
                                    id="product_callout.free_easy_returns"
                                    defaultMessage="Free & Easy Returns"
                                />
                            </Text>
                        </Flex>
                        <Box className="mt-product-suggest" id="mt-product-suggest"></Box>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
}

AddToCartDrawer.propTypes = {
    product: PropTypes.shape({
        name: PropTypes.string,
        imageGroups: PropTypes.array
    }),
    variant: PropTypes.shape({
        productId: PropTypes.string,
        variationValues: PropTypes.object
    }),
    priceData: PropTypes.any,
    heroImage: PropTypes.any,
    quantity: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.any
}

export const useAddToCartDrawer = () => {
    const [state, setState] = useState({isOpen: false, data: null})
    const {pathname} = useLocation()

    useEffect(() => {
        if (state.isOpen) {
            setState({...state, isOpen: false})
        }
    }, [pathname])

    return {
        isOpen: state.isOpen,
        data: state.data,
        onOpen: (data) => setState({isOpen: true, data}),
        onClose: () => setState({isOpen: false, data: null})
    }
}
